import type { PublicContent } from '@/lib/types/siteSettings';
import type { AvailabilityFiltersState, RangeBounds } from './availabilitiesFilters';

type AvailabilityRangeBounds = {
  monthlyRent: RangeBounds | null;
  areaSf: RangeBounds | null;
};

export function formatPluralCount(count: number, singular: string, plural: string): string {
  const label = count === 1 ? singular : plural;
  return `${count.toString()} ${label}`;
}

export function buildResultsSummary({
  filteredUnitCount,
  groupsCount,
  totalAvailableUnits,
  hasActiveFilters,
  availableGroupsCount,
}: {
  filteredUnitCount: number;
  groupsCount: number;
  totalAvailableUnits: number;
  hasActiveFilters: boolean;
  availableGroupsCount: number;
}): string {
  if (totalAvailableUnits === 0) {
    return 'No units available right now.';
  }
  const unitLabel = formatPluralCount(filteredUnitCount, 'unit', 'units');
  const addressLabel = formatPluralCount(groupsCount, 'address', 'addresses');
  const totalUnitLabel = formatPluralCount(totalAvailableUnits, 'unit', 'units');
  if (hasActiveFilters) {
    return `${unitLabel} across ${addressLabel} (of ${totalUnitLabel})`;
  }
  return `${totalUnitLabel} across ${formatPluralCount(availableGroupsCount, 'address', 'addresses')}`;
}

export function buildAvailabilityLabels(publicContent: PublicContent | null): {
  immediateLabel: string;
  negotiableLabel: string;
} {
  return {
    immediateLabel:
      publicContent?.propertyDetail.labels.unitAvailabilityImmediateLabel ?? 'Immediate',
    negotiableLabel:
      publicContent?.propertyDetail.labels.unitAvailabilityNegotiableLabel ?? 'Negotiable',
  };
}

export function clampAvailabilityFilters(
  filters: AvailabilityFiltersState,
  bounds: AvailabilityRangeBounds,
): AvailabilityFiltersState {
  return {
    ...filters,
    monthlyRent: {
      min: clampRangeValue(filters.monthlyRent.min, bounds.monthlyRent, 'min'),
      max: clampRangeValue(filters.monthlyRent.max, bounds.monthlyRent, 'max'),
    },
    areaSf: {
      min: filters.areaSf.min,
      max: filters.areaSf.max,
    },
  };
}

function clampRangeValue(
  value: number | null,
  bounds: RangeBounds | null,
  key: 'min' | 'max',
): number | null {
  if (!bounds || value === null || !Number.isFinite(value)) {
    return value ?? null;
  }

  const clamped = Math.min(Math.max(value, bounds.min), bounds.max);
  if (key === 'min') {
    return clamped <= bounds.min ? null : clamped;
  }
  return clamped >= bounds.max ? null : clamped;
}
